import { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Form, Modal, Typography } from "antd";

import CustomSelect from "@/components/inputs/CustomSelect";

import { getDefinitionOptions } from "@/api/definitions";
import {
  createField,
  getDefinitionFields,
  updateField,
} from "@/api/definitionFields";
import { getKeysOptions } from "@/api/nspd";

import { FIELD_TYPES_OPTIONS } from "@/constants/definitions";

const { Text } = Typography;
const DATE_FIELD_TYPES = ["date", "moment"];

const normalizeFields = (fields) => {
  if (Array.isArray(fields)) return fields;

  if (!fields || typeof fields !== "object") return [];

  return Object.values(fields).flatMap((value) =>
    Array.isArray(value) ? value : [],
  );
};

const getFieldType = (field) => field?.type;

// Окно для создания и редактирование полей объектов, содержит форму
const DefinitionFieldModal = ({
  data,
  definitionId,
  visible,
  useNSPD,
  nspdKeyCategoryUuid,
  onSave,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const typeValue = Form.useWatch("type", form);
  const objectValue = Form.useWatch("object", form);

  const [optionsPending, setOptionsPending] = useState(false);

  const [objects, setObjects] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [tables, setTables] = useState([]);
  const [tablesFields, setTablesFields] = useState({});
  const [polygonFields, setPolygonFields] = useState([]);
  const [parentTableFields, setParentTableFields] = useState([]);
  const [tableFieldsPending, setTableFieldsPending] = useState(false);
  const [loadedTableFieldsUuid, setLoadedTableFieldsUuid] = useState(null);

  const [nspdKeys, setNspdKeys] = useState([]);
  const [nspdKeysPending, setNspdKeysPending] = useState(false);
  const currentTableFields = useMemo(
    () => tablesFields[objectValue] || [],
    [tablesFields, objectValue],
  );
  const tableDateFields = currentTableFields.filter((field) =>
    DATE_FIELD_TYPES.includes(getFieldType(field)),
  );
  const isTableFieldsReady =
    typeValue === "table" &&
    !!objectValue &&
    loadedTableFieldsUuid === objectValue &&
    !tableFieldsPending;

  const fetchDefinitions = async () => {
    if (!typeValue) return;

    try {
      setOptionsPending(true);
      const [definitions, tables] = await Promise.all([
        getDefinitionOptions({
          type: typeValue,
        }),
        getDefinitionOptions({
          // type: "table",
          reference_definition_uuid: definitionId,
        }),
      ]);

      const options = definitions.map((item) => ({
        ...item,
        label: item.name,
      }));

      switch (typeValue) {
        case "object":
          setObjects(options);
          break;
        case "repository":
          setRepositories(options);
          break;

        default:
          break;
      }

      setTables(tables);
      setTablesFields((prev) =>
        tables.reduce((acc, table) => {
          const fields = normalizeFields(table.fields);
          acc[table.uuid] = prev[table.uuid]?.length ? prev[table.uuid] : fields;
          return acc;
        }, { ...prev }),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setOptionsPending(false);
    }
  };

  useEffect(() => {
    // сбросить значение в режиме "создание"
    if (!data) form.setFieldValue("object", null);
    fetchDefinitions();

    // eslint-disable-next-line
  }, [typeValue]);

  const fetchNSPDKeys = useCallback(async () => {
    if (!useNSPD || !nspdKeyCategoryUuid) return;

    try {
      setNspdKeysPending(true);

      const items = await getKeysOptions({
        nspd_category_uuid: nspdKeyCategoryUuid,
      });

      setNspdKeys(
        items.map((item) => ({
          ...item,
          label: item.name,
          value: item.uuid,
        })),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setNspdKeysPending(false);
    }
  }, [useNSPD, nspdKeyCategoryUuid]);

  useEffect(() => {
    if (visible) {
      fetchNSPDKeys();
    }
  }, [visible, fetchNSPDKeys]);

  useEffect(() => {
    if (!visible || !data) return;

    if (typeValue === "parent_polygon" && parentTableFields.length === 0)
      return;

    form.setFieldsValue({
      ...data,
      object: data.parent_table_field_uuid ?? data.object,
      child_polygon_field_uuid: data.child_polygon_field_uuid,
      due_date_field_uuid: data.due_date_field_uuid ?? null,
      nspd_key_uuid: data.nspd_key_uuid,
    });
  }, [visible, data, typeValue, parentTableFields, form]);

  useEffect(() => {
    if (typeValue !== "parent_polygon" || !data) return;

    const run = async () => {
      const parentField = data.parent_table_field;

      if (!parentField) return;

      const pf = await getDefinitionFields(parentField.object, true);

      const polygonFields = pf.filter((f) => f.type === "polygon");

      setPolygonFields(polygonFields);

      form.setFieldsValue({
        child_polygon_field_uuid: data.child_polygon_field_uuid,
      });
    };

    run();
  }, [typeValue, data, form]);

  useEffect(() => {
    if (typeValue !== "table" || !objectValue) return;

    let isActive = true;

    const run = async () => {
      try {
        setLoadedTableFieldsUuid(null);
        setTableFieldsPending(true);

        const fields = await getDefinitionFields(objectValue, true);

        if (!isActive) return;

        setTablesFields((prev) => ({
          ...prev,
          [objectValue]: normalizeFields(fields),
        }));
        setLoadedTableFieldsUuid(objectValue);
      } catch (error) {
        console.log(error);
      } finally {
        if (isActive) {
          setTableFieldsPending(false);
        }
      }
    };

    run();

    return () => {
      isActive = false;
    };
  }, [typeValue, objectValue]);

  useEffect(() => {
    let isActive = true;

    const run = async () => {
      if (!data && objectValue) {
        const fields = currentTableFields;

        if (typeValue === "table") {
          if (
            fields.length > 0 &&
            isActive &&
            !form.getFieldValue("relation_field_uuid")
          ) {
            form.setFieldValue("relation_field_uuid", fields[0].uuid);
          }
        }

        if (typeValue === "parent_polygon") {
          const selectedField = parentTableFields.find(
            (f) => f.uuid === objectValue,
          );

          if (!selectedField) return;

          const pf = await getDefinitionFields(selectedField.object, true);

          if (!isActive) return;

          const polygonFields = pf.filter((field) => field.type === "polygon");
          setPolygonFields(polygonFields);

          form.setFieldValue(
            "child_polygon_field_uuid",
            polygonFields[0]?.uuid || null,
          );
        }
      }
    };

    run();

    return () => {
      isActive = false;
    };
  }, [objectValue, typeValue, currentTableFields, parentTableFields, data, form]);

  useEffect(() => {
    if (typeValue !== "parent_polygon") return;

    getDefinitionFields(definitionId, true)
      .then((fields) => {
        const tableFields = fields.filter((f) => f.type === "table");

        setParentTableFields(tableFields);
      })
      .catch(console.error);
  }, [typeValue, definitionId]);

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    try {
      let payload = {
        ...values,
        nspd_key_uuid: form.getFieldValue("nspd_key_uuid") ?? null,
      };

      if (values.type === "parent_polygon") {
        payload = {
          ...payload,
          parent_table_field_uuid: values.object,
          object: undefined,
        };
      }

      if (data && values.type === "table") {
        payload = {
          ...payload,
          due_date_field_uuid: values.due_date_field_uuid ?? null,
        };
      }

      if (data) {
        const updatePayload = { ...data, ...payload };

        delete updatePayload.due_date_field;

        if (updatePayload.type !== "table") {
          delete updatePayload.due_date_field_uuid;
        }

        await updateField(updatePayload);
      } else {
        await createField({ descriptor: definitionId, ...payload });
      }

      onSave();
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () =>
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });

  const renderObjectField = (options, label, message) => (
    <Form.Item
      name="object"
      label={label}
      rules={[{ required: true, message }]}
    >
      <CustomSelect
        loading={optionsPending}
        placeholder="Начните вводить"
        options={options}
        showSearch
        allowClear
        fieldNames={{ value: "uuid" }}
        optionFilterProp="label"
        disabled={data}
      />
    </Form.Item>
  );

  return (
    <Modal
      maskClosable={false}
      title={data ? "Редактирование поля" : "Добавление нового поля"}
      open={visible}
      onOk={onOk}
      onCancel={handleCancel}
      okText={data ? "Сохранить" : "Добавить"}
      cancelText="Отмена"
      okButtonProps={{ loading: isLoading }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={data}
        onFinish={handleSave}
      >
        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: "Пожалуйста, введите название" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input.TextArea rows={5} />
        </Form.Item>

        <Form.Item
          name="type"
          label="Тип"
          rules={[{ required: true, message: "Пожалуйста, выберите тип" }]}
        >
          <CustomSelect
            showSearch
            placeholder="Выберите тип"
            optionFilterProp="label"
            options={FIELD_TYPES_OPTIONS}
            disabled={data}
          />
        </Form.Item>

        {typeValue === "object" &&
          renderObjectField(objects, "Объект", "Пожалуйста, выберите объект")}
        {typeValue === "repository" &&
          renderObjectField(
            repositories,
            "Справочник",
            "Пожалуйста, выберите справочник",
          )}
        {typeValue === "table" && (
          <>
            <Form.Item
              name="object"
              label="Таблица"
              rules={[
                { required: true, message: "Пожалуйста, выберите таблицу" },
              ]}
            >
              <CustomSelect
                loading={optionsPending}
                placeholder="Начните вводить"
                options={tables}
                showSearch
                fieldNames={{ value: "uuid", label: "name" }}
                optionFilterProp="name"
                disabled={data}
              />
            </Form.Item>

            {!!objectValue && (
              <>
                <Form.Item
                  name="relation_field_uuid"
                  label="Поле"
                  rules={[
                    { required: true, message: "Пожалуйста, выберите поле" },
                  ]}
                >
                  <CustomSelect
                    loading={optionsPending || tableFieldsPending}
                    placeholder="Начните вводить"
                    options={currentTableFields}
                    showSearch
                    fieldNames={{ value: "uuid", label: "name" }}
                    optionFilterProp="name"
                    disabled={data}
                  />
                </Form.Item>

                {data && (
                  <Form.Item
                    name="due_date_field_uuid"
                    label="Поле срока исполнения"
                  >
                    <CustomSelect
                      loading={optionsPending || tableFieldsPending}
                      placeholder={
                        isTableFieldsReady ? "Выберите поле" : "Загрузка..."
                      }
                      options={tableDateFields}
                      showSearch
                      allowClear
                      fieldNames={{ value: "uuid", label: "name" }}
                      optionFilterProp="name"
                      disabled={!isTableFieldsReady}
                    />
                  </Form.Item>
                )}
              </>
            )}
          </>
        )}
        {typeValue === "parent_polygon" && (
          <>
            <Form.Item
              name="object"
              label="Таблица"
              rules={[
                { required: true, message: "Пожалуйста, выберите таблицу" },
              ]}
            >
              <CustomSelect
                loading={optionsPending}
                placeholder="Начните вводить"
                options={parentTableFields}
                showSearch
                fieldNames={{ value: "uuid", label: "name" }}
                optionFilterProp="name"
                disabled={data}
              />
            </Form.Item>

            {!!objectValue && (
              <Form.Item
                name="child_polygon_field_uuid"
                label="Поле"
                rules={[
                  { required: true, message: "Пожалуйста, выберите поле" },
                ]}
              >
                <CustomSelect
                  loading={optionsPending}
                  placeholder="Начните вводить"
                  options={polygonFields}
                  showSearch
                  fieldNames={{ value: "uuid", label: "name" }}
                  optionFilterProp="name"
                  disabled={data}
                />
              </Form.Item>
            )}
          </>
        )}
        {typeValue === "mask" && (
          <>
            <Form.Item
              name="regexp"
              label="Регулярное выражение"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите регулярное выражение",
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();

                    try {
                      new RegExp(value);
                      return Promise.resolve();
                    } catch {
                      return Promise.reject(
                        new Error("Некорректное регулярное выражение"),
                      );
                    }
                  },
                },
              ]}
            >
              <Input placeholder="Например: ^\d{2}\.\d{2}\.\d{4}$" />
            </Form.Item>

            <Form.Item
              name="example"
              label="Пример"
              dependencies={["regexp"]}
              rules={[
                { required: true, message: "Пожалуйста, введите пример" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const regexpKey = getFieldValue("regexp");

                    if (!value || !regexpKey) return Promise.resolve();

                    try {
                      const regexp = new RegExp(regexpKey);

                      if (regexp.test(value)) {
                        return Promise.resolve();
                      }

                      return Promise.reject(
                        new Error(
                          "Пример не соответствует регулярному выражению",
                        ),
                      );
                    } catch {
                      return Promise.resolve();
                    }
                  },
                }),
              ]}
            >
              <Input placeholder="Введите пример значения" />
            </Form.Item>
          </>
        )}
        {useNSPD && (
          <Form.Item name="nspd_key_uuid" label="Связь с полем запроса НСПД">
            <CustomSelect
              loading={nspdKeysPending}
              placeholder="Выберите поле из запроса НСПД"
              options={nspdKeys}
              showSearch
              allowClear
              fieldNames={{ value: "uuid", label: "name" }}
              optionFilterProp="name"
            />
          </Form.Item>
        )}

        {/* <Form.Item name="temporal" label="Временное поле">
          <Switch disabled={data} />
        </Form.Item> */}

        {error && <Text type="danger">{error}</Text>}
      </Form>
    </Modal>
  );
};

export default DefinitionFieldModal;
